import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { endOfDay } from "date-fns/endOfDay";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FuelCellGrid } from "./components/FuelCellGrid";
import { FuelCellSummary } from "./components/FuelCellSummary";
import { FuelCellUsageChart } from "./components/FuelCellUsageChart";
import { useFuelCellData } from "./hooks/useFuelCellData";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { usePersistedDateRange } from "@/hooks/userState/usePersistedDateRange";
import { mapLazy } from "@/utilities/mapLazy";

const FilterStackLayout = mapLazy(
  "FilterStackLayout",
  import("@/components/layout/FilterStackLayout"),
);

/**
 * Below this many kilometres no average consumption is reported by default. Over a couple of
 * kilometres the figure is dominated by whatever the tank sensor read at each end.
 */
const DEFAULT_MIN_DISTANCE_KM = 30;

export function FuelCellPage() {
  const { t } = useTranslation();
  const [minDistanceKm, setMinDistanceKm] = useState(DEFAULT_MIN_DISTANCE_KM);
  const { fromDate, toDate, setRange, isReady } = usePersistedDateRange({
    key: "dateRange:fuelCell",
    defaultLookbackDays: 7,
  });
  const {
    rows,
    samplesByDevice,
    isLoading,
    isFetching,
    error,
    groupMissing,
    devicesTruncated,
    summaryStats,
  } = useFuelCellData(fromDate, toDate, minDistanceKm, isReady);

  // Counted rather than left to the grey text alone: a reader who has not been told what the
  // grey means reads it as a styling quirk, and the point of it is that those tank figures
  // cannot be relied on.
  const staleCount = rows.filter(row => row.isStale).length;

  return (
    <FilterStackLayout
      minHeight={60}
      filters={
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <DateRangeSelect
            fromDate={fromDate}
            toDate={toDate}
            onChange={(from, to) => {
              // A start after the stored end arrives as [start, null]: write it as a same-day
              // range in one update instead of leaving an inverted pair for the picker to trip on.
              setRange(from, to ?? (from ? endOfDay(from) : undefined));
            }}
          />
          <TextField
            type="number"
            size="small"
            label={t("fuelCell.minDistance")}
            helperText={t("fuelCell.minDistanceHint")}
            value={minDistanceKm}
            onChange={event => {
              const value = Number(event.target.value);
              setMinDistanceKm(Number.isFinite(value) && value > 0 ? value : 0);
            }}
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
            sx={{ width: { xs: "100%", sm: 260 } }}
          />
          <Button
            variant="outlined"
            onClick={() => setMinDistanceKm(0)}
            disabled={minDistanceKm === 0}
          >
            {t("fuelCell.resetMinDistance")}
          </Button>
        </Stack>
      }
      content={
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ mx: 2 }}>
              {error.message}
            </Alert>
          )}
          {groupMissing && (
            <Alert severity="warning" sx={{ mx: 2 }}>
              {t("fuelCell.groupMissing", "Group '07 Fuel Cell' not found in this database")}
            </Alert>
          )}
          {devicesTruncated && (
            <Alert severity="warning" sx={{ mx: 2 }}>
              {t("fuelCell.devicesTruncated", { count: RESULTS_LIMIT_DEVICE })}
            </Alert>
          )}
          {staleCount > 0 && (
            <Alert severity="warning" sx={{ mx: 2 }}>
              {t(
                "fuelCell.staleNotice",
                "{{count}} vehicle(s) drove without reporting a tank level. Their tank figures are greyed out and cannot be relied on.",
                { count: staleCount },
              )}
            </Alert>
          )}
          {rows.length > 0 && <FuelCellSummary stats={summaryStats} />}
          {rows.length > 0 && <FuelCellUsageChart rows={rows} />}
          <FuelCellGrid
            rows={rows}
            samplesByDevice={samplesByDevice}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={!!error}
            fromDate={fromDate}
            toDate={toDate}
          />
        </Box>
      }
    />
  );
}
